/**
 * Customer Deleted List Hook
 */
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getDeletedCustomers } from '@/modules/Customer/services'
import {
    getDeletedColumns,
    DELETED_FILTER_DEFAULTS,
    DELETED_SORT_CONFIG,
} from '../utils/customer-deleted-config'

interface DeletedFilters {
    q: string
}

export const useCustomerDeletedList = () => {
    const { t } = useTranslation('customer')

    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<DeletedFilters>({
        defaults: {
            page: 1,
            page_size: 20,
            ...DELETED_SORT_CONFIG.defaultSort,
        },
        filterDefaults: DELETED_FILTER_DEFAULTS,
    })

    const { control } = useForm<DeletedFilters>({
        defaultValues: filters,
        values: filters,
    })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS, 'deleted', queryParams],
        queryFn: () =>
            getDeletedCustomers({
                page: params.page,
                page_size: params.page_size,
                q: queryParams.q,
                sort_by: params.sort_by,
                sort_order: params.sort_order,
                filters: queryParams.filters,
            }),
        placeholderData: true,
    })

    const customers = data?.results || []
    const pagination = data?.pagination

    const columns = useMemo(() => getDeletedColumns(t), [t])

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (size: number) => setParams({ page_size: size }),
        [setParams]
    )
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    return {
        customers,
        pagination,
        stateComponent: renderState(),
        params,
        control,
        setFilter,
        searchInputProps,
        columns,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        t,
    }
}
